import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ArcElement, Chart, ChartConfiguration, ChartData, Legend, PieController, Title, Tooltip } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { PieChartConfig, PieChartData } from './pie-chart.interface';

// Register Chart.js components
Chart.register(PieController, ArcElement, Title, Tooltip, Legend);

// Color palette based on app's design system (same as bar chart)
const CHART_COLORS = {
  primary: '#ed3c6a', // $raspberry-darker
  secondary: '#9571c3', // $grape
  warning: '#ffc74c', // $lemon-darker
  error: '#d43b3b', // $rose-darker
  success: '#47c693', // $grass-darker
  // Additional colors for larger datasets
  night: '#5c576a', // $night
  grapeDarker: '#230b41', // $grape-darker
  raspberry: '#f69db4', // $raspberry
};

const DEFAULT_COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.warning,
  CHART_COLORS.error,
  CHART_COLORS.success,
  CHART_COLORS.night,
  CHART_COLORS.grapeDarker,
  CHART_COLORS.raspberry,
];

@Component({
  selector: 'ui-pie-chart',
  standalone: true,
  imports: [CommonModule, BaseChartDirective],
  templateUrl: './pie-chart.component.html',
})
export class PieChartComponent implements OnInit {
  @Input() data!: PieChartData;
  @Input() config: PieChartConfig = {};

  chartData!: ChartData<'pie'>;
  chartOptions!: ChartConfiguration<'pie'>['options'];

  ngOnInit(): void {
    this.setupChartData();
    this.setupChartOptions();
  }

  private setupChartData(): void {
    if (!this.data) return;

    this.chartData = {
      labels: this.data.labels,
      datasets: this.data.datasets.map((dataset) => ({
        label: dataset.label,
        data: dataset.data,
        backgroundColor: dataset.backgroundColor || this.generateDefaultColors(dataset.data.length),
        borderColor: '#ffffff',
        borderWidth: 2,
      })),
    };
  }

  private generateDefaultColors(count: number): string[] {
    const colors: string[] = [];
    for (let i = 0; i < count; i++) {
      colors.push(DEFAULT_COLORS[i % DEFAULT_COLORS.length]);
    }
    return colors;
  }

  private setupChartOptions(): void {
    this.chartOptions = {
      responsive: this.config.responsive !== false,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: this.config.showLegend !== false,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              family: 'Inter, sans-serif',
              size: 12,
            },
            color: '#5c576a', // $night
          },
        },
        tooltip: {
          backgroundColor: 'rgba(23, 20, 28, 0.9)', // $night-darker with opacity
          titleFont: {
            family: 'Inter, sans-serif',
            size: 13,
          },
          bodyFont: {
            family: 'Inter, sans-serif',
            size: 12,
          },
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: 'rgba(229, 226, 233, 0.2)', // $smoke with opacity
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          padding: 12,
          callbacks: {
            label: function (context) {
              const label = context.label || '';
              const value = context.parsed;
              const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0) as number;
              const percentage = ((value / total) * 100).toFixed(1);
              return `${label}: ${value} (${percentage}%)`;
            },
          },
        },
      },
      animation: {
        duration: 750,
        easing: 'easeInOutQuart',
      },
      interaction: {
        intersect: false,
      },
    };
  }

  hasEmptyData(): boolean {
    if (!this.data || !this.data.datasets || this.data.datasets.length === 0) {
      return true;
    }
    return this.data.datasets.some(dataset => !dataset.data || dataset.data.length === 0);
  }
}
